import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { recipesApi }     from '@/api/recipes';
import { ingredientsApi } from '@/api/ingredients';
import { suppliersApi }   from '@/api/suppliers';
import { PageHeader, Card, CardHeader, CardTitle, CardContent, Spinner, Badge } from '@/components/ui';
import { IceCream2, Package, Truck, BookOpen, TrendingUp, TrendingDown } from 'lucide-react';

export default function DashboardPage() {
  const { t } = useTranslation();

  const { data: recipes,     isLoading: lr } = useQuery({ queryKey: ['recipes', { page: 1, limit: 1 }],     queryFn: () => recipesApi.list({ page: 1, limit: 1 }) });
  const { data: ingredients, isLoading: li } = useQuery({ queryKey: ['ingredients', { page: 1, limit: 1 }], queryFn: () => ingredientsApi.list({ page: 1, limit: 1 }) });
  const { data: suppliers,   isLoading: ls } = useQuery({ queryKey: ['suppliers', { page: 1, limit: 1 }],   queryFn: () => suppliersApi.list({ page: 1, limit: 1 }) });
  const { data: ranking,     isLoading: lm } = useQuery({ queryKey: ['margin-ranking'],                       queryFn: recipesApi.marginRanking });

  const stats = [
    { label: t('dashboard.totalRecipes'),     value: recipes?.meta.total,     icon: <BookOpen size={20} className="text-blue-500" />,   href: '/recipes' },
    { label: t('dashboard.totalIngredients'), value: ingredients?.meta.total, icon: <Package size={20} className="text-green-500" />,  href: '/ingredients' },
    { label: t('dashboard.totalSuppliers'),   value: suppliers?.meta.total,   icon: <Truck size={20} className="text-orange-500" />,   href: '/suppliers' },
  ];

  return (
    <div>
      <PageHeader title={t('dashboard.title')}>
        <IceCream2 size={22} className="text-primary" />
      </PageHeader>

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mb-8">
        {stats.map(stat => (
          <Link key={stat.href} to={stat.href} className="group">
            <Card className="hover:shadow-md transition-shadow">
              <CardContent className="flex items-center gap-4 p-5">
                <div className="rounded-lg bg-muted p-2.5">{stat.icon}</div>
                <div>
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                  <p className="text-2xl font-bold">
                    {lr || li || ls ? '—' : (stat.value ?? 0)}
                  </p>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Margin ranking */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Best margins */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp size={18} className="text-green-500" />
              Top 5 Best Margin
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {lm ? (
              <Spinner className="py-8" />
            ) : !ranking?.best.length ? (
              <p className="py-6 text-center text-sm text-muted-foreground">No recipes with selling price yet</p>
            ) : (
              <ul className="divide-y divide-border">
                {ranking.best.map(r => (
                  <li key={r.id} className="py-2.5">
                    <Link to={`/recipes/${r.id}`} className="flex items-center justify-between hover:text-primary transition-colors">
                      <span className="font-medium truncate mr-2">{r.name}</span>
                      <Badge variant="success">{r.actual_margin_pct.toFixed(1)}%</Badge>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Worst margins */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingDown size={18} className="text-destructive" />
              Top 5 Worst Margin
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {lm ? (
              <Spinner className="py-8" />
            ) : !ranking?.worst.length ? (
              <p className="py-6 text-center text-sm text-muted-foreground">No recipes with selling price yet</p>
            ) : (
              <ul className="divide-y divide-border">
                {ranking.worst.map(r => (
                  <li key={r.id} className="py-2.5">
                    <Link to={`/recipes/${r.id}`} className="flex items-center justify-between hover:text-primary transition-colors">
                      <span className="font-medium truncate mr-2">{r.name}</span>
                      <Badge variant={r.actual_margin_pct < 0 ? 'destructive' : 'secondary'}>{r.actual_margin_pct.toFixed(1)}%</Badge>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
